import closest from 'closest';
import { parentsUntil } from '../utils';

/**
 * Cross-browser function for dispatching events.
 * @param {EventTarget} target The event target (e.g. an HTML element)
 * @param {string} eventType The event type (e.g. "click")
 * @param {boolean} bubbles Whether the event should bubble (defaults to `false`)
 * @param {boolean} cancelable Whether the event is cancelable (defaults to `false`)
 */
function dispatchEvent(target, eventType, bubbles = false, cancelable = false) {
  if (typeof Event === 'function') {
    let event = new Event(eventType, { bubbles, cancelable });
  } else {
    let event = document.createEvent('Event');
    event.initEvent(eventType, bubbles, cancelable);
  }

  target.dispatchEvent(event);
}

/**
 * Updates the checked state marker of the wrapping `label` to match the provided checkbox’ props
 * @param {HTMLElement} checkbox The checkbox element (input[type="checkbox"])
 */
function updateCheckboxAria(checkbox) {
  closest(checkbox, 'label').setAttribute(
    'data-checked',
    checkbox.indeterminate ? 'mixed' : checkbox.checked ? 'true' : 'false'
  );
}

/**
 * Sets the `checked` and `indeterminate` props of a checkbox to the correct values for a given status. Also updates the checked-state marker of the wrapping `label` element.
 * @param {HTMLElement} checkbox The checkbox element (input[type="checkbox"])
 * @param {mixed} checked Set to `true` for checked, `false` for unchecked, or any other value for indeterminate/mixed state.
 */
function changeCheckboxStatus(checkbox, checked) {
  switch (checked) {
    case true:
      checkbox.indeterminate = false;
      checkbox.checked = true;
      break;
    case false:
      checkbox.indeterminate = false;
      checkbox.checked = false;
      break;
    default:
      checkbox.checked = false;
      checkbox.indeterminate = true;
      break;
  }
  updateCheckboxAria(checkbox);
}

/**
 * Updates the status of a checkbox based on the status of its children.
 * @param {HTMLELement} checkbox The checkbox to update (input[type="checkbox"])
 * @param {HTMLElement} item (Optional) The wrapping item, if already known (for performance)
 */
function updateCheckboxStatus(checkbox, item) {
  if (!item) {
    item = closest(checkbox, '.vgr-checkbox-tree__item');
  }
  let ul = item.querySelector('ul');
  if (!ul) {
    return;
  }
  let checkboxCount = ul.querySelectorAll('input[type="checkbox"]').length;
  if (!checkboxCount) {
    return;
  }
  let checkedCount = ul.querySelectorAll('input[type="checkbox"]:checked').length;
  if (checkedCount === 0) {
    changeCheckboxStatus(checkbox, false);
    return;
  }
  if (checkedCount === checkboxCount && checkboxCount > 1) {
    changeCheckboxStatus(checkbox, true);
    return;
  }
  changeCheckboxStatus(checkbox, null);
}

function propagateCheckboxChange(checkbox) {
  // Update child items
  let item = closest(checkbox, '.vgr-checkbox-tree__item');

  let childCheckboxes = Array.from(item.querySelectorAll('input[type="checkbox"]')).slice(1);

  childCheckboxes.forEach((childCheckbox) => {
    changeCheckboxStatus(childCheckbox, checkbox.checked);
  });

  // Update parent items
  let parents = parentsUntil(item, '.vgr-checkbox-tree', '.vgr-checkbox-tree__item');
  parents.forEach((parent) => updateCheckboxStatus(parent.querySelector('input[type="checkbox"]'), parent));
}

export default class CheckboxTree {

  checkboxes = [];
  expandButtons = [];

  changeCheckboxStatusByLabel = (label, checked) => {
    let checkbox = this.element.querySelector(`input[data-label="${label}"]`);
    if (!checkbox) {
      return;
    }
    changeCheckboxStatus(checkbox, checked);
    propagateCheckboxChange(checkbox);
  };

  destroy = () => {
    console.log('CALLING DESTROY');
    // Element
    this.element.removeEventListener('change', this.elementChangeHandler);
    // Checkboxes
    this.checkboxes.forEach((el) => {
      el.removeEventListener('keydown', this.keyboardChangeHandler);
    });
    // Expand buttons
    this.expandButtons.forEach((el) =>
      el.removeEventListener('click', this.expandButtonChangeHandler)
    );
  };

  elementChangeHandler = (event) => {
    let checkbox = event.target;
    if (checkbox.nodeName !== 'INPUT' || checkbox.type !== 'checkbox') {
      return;
    }
    updateCheckboxAria(checkbox);
    propagateCheckboxChange(checkbox);
    dispatchEvent(checkbox, 'checkboxTreeChange', true);
  };

  keyboardChangeHandler = (event) => {
    if (event.key === ' ' || event.key === 'Spacebar') {
      event.preventDefault();
      let checkbox = el.querySelector('input[type="checkbox"]');
      changeCheckboxStatus(checkbox, !checkbox.checked);
      dispatchEvent(checkbox, 'change', true);
      dispatchEvent(checkbox, 'checkboxTreeChange', true);
    }
  };

  expandButtonChangeHandler = (event) => {
    event = event || window.event;
    let target = event.currentTarget || event.target;
    let item = closest(target, '.vgr-checkbox-tree__item');
    if (item) {
      let newState = item.getAttribute('aria-expanded') === 'true' ? 'false' : 'true';
      item.setAttribute('aria-expanded', newState);
    }
  };

  constructor(element) {
    this.element = element;
    element.checkboxTree = this;

    Array.from(element.querySelectorAll('.vgr-checkbox-tree__checkbox')).forEach((el) => {
      this.checkboxes.push(el);
      el.addEventListener('keydown', this.keyboardChangeHandler);
    });

    Array.from(element.querySelectorAll('input[type="checkbox"]')).forEach((checkbox) =>
      updateCheckboxStatus(checkbox)
    );

    element.addEventListener('change', this.elementChangeHandler);

    Array.from(element.querySelectorAll('.vgr-checkbox-tree__toggle')).forEach((el) => {
      this.expandButtons.push(el);
      el.addEventListener('click', this.expandButtonChangeHandler);
    }
    );
  }
}
